<template>
  <div class="load-simulation__buttons-wrapper d-flex flex-column mb-2">
    <b-file
      id="fileImporter"
      class="d-none"
      ref="fileImporter"
      v-model="fileRef"
      no-drop
      accept=".xlsx"
      @input="onInputFile"
    />
    <b-dropdown
      class="ml-auto"
      right
      split
      menu-class="w-100"
      variant="outline-green"
      :popper-opts="this.popperOptions"
      :disabled="isLoading || loading">
      <template #button-content>
        <div @click="onClickImportButtonHandler">
          <span class="fas fa-file-excel"/>
          {{ $t('engineering.load-simulation.import-xlsx') }}
        </div>
      </template>
      <b-dropdown-item-button>
        <b-btn
          block
          variant="outline-green"
          @click="downloadTemplate">
          {{ $t('engineering.load-simulation.download-template') }}
        </b-btn>
      </b-dropdown-item-button>
    </b-dropdown>
    <div class="d-flex justify-content-end mt-2">
      <div class="mr-2 d-flex">
        <s-loader
          v-if="isLoading"
          size="sm"
        />
        <i
          v-else
          aria-hidden="true"
          :class="['mt-1', fileStatus.icon]"
        />
      </div>
      <div
        class="d-flex flex-column"
        v-if="file">
        <div>
          <span>{{ fileName }}</span>
          <span class="load-simulation__excel-button-file-size">{{ fileSize }}</span>
        </div>
        <div class="font-italic load-simulation__excel-button--small">
          <span :class="fileStatus.color">{{ fileStatus.label }} </span>
          <span v-if="haveProcessingErrors && !loading">
            {{ $t('engineering.load-simulation.errorLog.click') }}
            <a
              class="load-simulation__excel-button-anchor"
              @click="downloadErrorLog">
              {{ $t('engineering.load-simulation.errorLog.here') }}
            </a>
            {{ $t('engineering.load-simulation.errorLog.toDownload') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import store from '@/store';
import loadSimulationService from '@/domain/services/installation/engineering/loadSimulation/loadSimulationService';
import { LoadSimulationProcessingStatus } from 'sigma-mappings';
import eventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import popperOptsMixin from '@/services/popperOptsMixin';

const FETCH_STATUS_DELAY = 5000;

const fileImportingStatus = {
  none: 0,
  loading: 1,
  errorInImportation: 2,
  errorInProcessing: 3,
  importedFile: 4,
};

export default {
  name: 'LoadSimulationImportExcelButton',
  mixins: [popperOptsMixin],
  props: {
    hasData: {
      type: Boolean,
      default: () => false,
    },
    importData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
    },
  },
  data: vm => ({
    file: vm.importData?.file || { name: '', size: 0 },
    fileRef: null,
    fetchingDataInterval: null,
    currentStatus: vm.importData?.currentStatus || fileImportingStatus.none,
    warningNumber: vm.importData?.warningNumber || 0,
    pointsImported: vm.importData?.pointsImported || 0,
    fileId: vm.importData?.fileId || '',
  }),
  computed: {
    status: {
      get() {
        return this.currentStatus;
      },
      set(value) {
        this.currentStatus = value;
        this.$emit('update:loading', value === fileImportingStatus.loading);
      },
    },
    fileName() {
      return this.file?.name || '';
    },
    fileSize() {
      if (!this.file?.size) {
        return '';
      }

      const megaByte = this.file.size / 1000000;
      const fileSizeRounded = Math.round(megaByte * 1000) / 1000;
      return `(${fileSizeRounded} mb)`;
    },
    fileStatus() {
      if (this.status === fileImportingStatus.loading) {
        return {
          label: this.$t('engineering.load-simulation.importFileMessage.loading'),
          icon: 'd-none',
          color: 'load-simulation__excel-button--success',
        };
      }

      if (this.status === fileImportingStatus.errorInProcessing
        || this.status === fileImportingStatus.errorInImportation) {
        return {
          label: this.$t('engineering.load-simulation.importFileMessage.error'),
          icon: 'icon-error',
          color: 'load-simulation__excel-button--error',
        };
      }

      if (this.warningNumber > 0) {
        return {
          label: `${this.warningNumber} ${this.$t('engineering.load-simulation.importFileMessage.warning')}`,
          icon: 'icon-file-ripped',
          color: 'load-simulation__excel-button--warning',
        };
      }

      if (this.status === fileImportingStatus.importedFile) {
        return {
          label: `${this.pointsImported} ${this.$t('engineering.load-simulation.importFileMessage.success')}`,
          icon: 'icon-file',
          color: 'load-simulation__excel-button--success',
        };
      }

      return {
        label: '',
        icon: 'd-none',
        color: 'load-simulation__excel-button--success',
      };
    },
    haveProcessingErrors() {
      return this.warningNumber > 0
        || this.status === fileImportingStatus.errorInProcessing;
    },
    isLoading() {
      return this.status === fileImportingStatus.loading;
    },
  },
  created() {
    this.initEvents();
  },
  beforeDestroy() {
    if (this.fetchingDataInterval) {
      this.stopFetchingStatus();
    }
    this.removeEvents();
  },
  methods: {
    downloadTemplate() {
      loadSimulationService.downloadTemplate();
    },
    downloadErrorLog() {
      const { assetModuleId } = this.$route.params;
      loadSimulationService.downloadLog(assetModuleId, this.fileId);
    },
    forceFileInputClick() {
      this.$refs.fileImporter.$el
        .querySelector('#fileImporter')
        .click();
    },
    onClickImportButtonHandler() {
      if (!this.hasData) {
        this.forceFileInputClick();
        return;
      }

      this.$confirmMessage({ code: 'MC052' })
        .accepted(() => {
          this.forceFileInputClick();
        });
    },
    importExcel() {
      this.status = fileImportingStatus.loading;
      this.warningNumber = 0;
      this.pointsImported = 0;
      this.fileId = '';
      const { assetModuleId } = this.$route.params;

      this.$emit('update:loading', true);

      loadSimulationService.processPoints(assetModuleId, this.file)
        .then((fileId) => {
          this.fileId = fileId;
          this.fetchProcessingStatus();
          this.setFetchingInterval();
        })
        .catch(() => {
          this.status = fileImportingStatus.errorInImportation;
          this.$emit('update:loading', false);
        });
    },
    setFetchingInterval() {
      this.fetchingDataInterval = setInterval(
        () => this.fetchProcessingStatus(),
        FETCH_STATUS_DELAY,
      );
    },
    fetchProcessingStatus() {
      const successStatus = [
        LoadSimulationProcessingStatus.SUCCESSFULLY_PROCESSED,
        LoadSimulationProcessingStatus.PROCESSED_WITH_WARNINGS,
      ];

      const failStatus = [
        LoadSimulationProcessingStatus.PROCESSING_ERROR,
      ];

      loadSimulationService.getProcessingStatus(this.$route.params.assetModuleId, this.fileId)
        .then((data) => {
          const status = data.step.situation;

          if (successStatus.includes(status)) {
            this.informSuccessInFileImportation(data);
            return;
          }

          if (failStatus.includes(status)) {
            this.informErrorInFileImportation(data);
          }
        })
        .catch(() => this.informErrorInFileImportation())
        .finally(() => this.$emit('update:loading', false));
    },
    showImportResumeMessage(data) {
      const { pointsSuccessfullyImported, roundedFieldsNumber, errorFieldsNumber } = data;
      this.$pushAlert({
        code: 'MI040',
        messageParams: {
          0: pointsSuccessfullyImported,
          1: roundedFieldsNumber,
          2: errorFieldsNumber,
        },
      });
    },
    showImportErrorsMessages({ errors } = {}) {
      (errors || []).forEach((error) => {
        const [parameter] = error.parameters;
        switch (error.messageCode) {
          case 'ME017':
            this.$pushAlert({
              code: 'ME017',
              messageParams: { allowedExtensionList: parameter },
            });
            break;
          case 'ME018':
            this.$pushAlert({ code: 'ME018' });
            break;
          case 'ME033':
            this.$pushAlert({ code: 'ME033' });
            break;
          case 'ME069':
            this.$pushAlert({
              code: 'ME069',
              messageParams: { 0: parameter },
            });
            break;
          default:
        }
      });
    },
    storeDispatch() {
      const { name, size } = this.file;
      const {
        currentStatus,
        warningNumber,
        pointsImported,
        fileId,
      } = this;
      const importData = {
        currentStatus,
        warningNumber,
        pointsImported,
        fileId,
        file: {
          name,
          size,
        },
      };
      store.dispatch('setSimulationDataImportData', importData);
    },
    informSuccessInFileImportation(data) {
      this.pointsImported = data.points.length;
      this.warningNumber = data.points
        .filter(x => x.errors?.loading?.length > 0).length;

      this.warningNumber += data.points
        .filter(x => x.errors?.environmentTemperature?.length > 0).length;

      this.status = fileImportingStatus.importedFile;
      eventBus.emit(events.onImportSimulation, data);
      this.stopFetchingStatus();
      this.showImportResumeMessage(data);
      this.showImportErrorsMessages(data);
      this.storeDispatch();
    },
    informErrorInFileImportation(data) {
      this.status = fileImportingStatus.errorInProcessing;
      this.stopFetchingStatus();
      this.showImportErrorsMessages(data);
      this.storeDispatch();
    },
    stopFetchingStatus() {
      clearInterval(this.fetchingDataInterval);
      this.fetchingDataInterval = null;
    },
    clearFile() {
      if (this.$refs.fileImporter?.reset) {
        this.$refs.fileImporter.reset();
      }
      this.file = null;
    },
    initEvents() {
      eventBus.on(events.onSimulationPointsListEmpty, this.clearFile, this);
    },
    removeEvents() {
      eventBus.off(events.onSimulationPointsListEmpty, this.clearFile, this);
    },
    onInputFile(file) {
      if (file !== null) {
        this.clearFile();
        this.file = file;
        this.importExcel();
      }
    },
  },
};
</script>
<style lang="sass" scoped>
.load-simulation__buttons-wrapper
  ::v-deep
    .dropdown
      & button:first-child
        padding: 0
      & .buttons-wrapper__upload-button
        height: 100%
        display: flex
        flex-direction: column
        justify-content: center
        align-items: center
    .dropdown-menu.show
      padding: 0
      width: 100%
      border-radius: 4px
    .dropdown-item
      padding: 0
    .loading
      position: relative
      top: -0.5em
      left: -0.5em
  & .load-simulation
    &__excel-button
      &--success
        color: var(--color-success)
      &--error
        color: var(--color-danger)
      &--warning
        color: var(--color-warning)
      &--small
        font-size: 0.85rem
    &__excel-button-file-size
      font-size: 0.85rem
      color: var(--color-gray-intermediary)
    &__excel-button-anchor
      color: var(--color-info)
      text-decoration: underline
      &:hover
        cursor: pointer
</style>
